import React from 'react';
import { NavBar } from '@design-system/NavBar';
import { Separator } from '@design-system/Separator';
import { Skeleton } from '@design-system/Skeleton';
import { StandardContainer } from '@design-system/StandardContainer';
import { ConditionScoreCard } from '@design-system/Cards/ConditionScoreCard';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { InfoSheet } from '@screens/HealthPages/InfoSheet';
import { INFO_SHEET_CONTENT } from '@/utils/InfoSheet/InfoSheetContent';
import { useNavigate } from 'react-router-dom';
import { EventTracker } from '@/utils';

interface ConditionDetailProps {
  name: string;
  rank: string;
  rank_color: string;
  reason: string;
  links: string[];
  score: number;
  result: number;
}

interface HealthConditionDetailContainerProps {
  condition: ConditionDetailProps;
  conditionName: string | undefined;
  scoreId: number;
  isLoading: boolean;
}

// Skeleton component for scores
const ScoresSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
    {Array.from({ length: 4 }).map((_, index) => (
      <Skeleton
        key={`score-skeleton-${index}`}
        className="h-[78px] w-full rounded-lg"
      />
    ))}
  </div>
);

// Skeleton component for links
const ResearchSkeleton = () => (
  <div className="px-5 pt-5 pb-0.5 rounded-sm bg-[#42434D4D] mt-6">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={`research-skeleton-${index}`}>
        <Skeleton className="w-full h-6 mb-4" />
        {index < 3 && <Separator className="bg-stroke-primary-subtle mb-4" />}
      </div>
    ))}
  </div>
);

const onClickData = (link: string) => {
  EventTracker.trackEvent(
    'health',
    `health_healthConnections_L4-Condition_research`,
  );
  window.open(link, '_blank', 'noopener,noreferrer');
};

export const HealthConditionDetailContainer: React.FC<
  HealthConditionDetailContainerProps
> = ({ condition, isLoading, scoreId }) => {
  const { name, score, rank, rank_color, result, reason, links } = condition || {};
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <StandardContainer>
        <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-8" />

        {isLoading ? (
          <Skeleton className="h-8 w-[70%] rounded-md mt-6" />
        ) : (
          <>
            {reason && (
              <div className="flex flex-col gap-4 mt-12">
                <h1 className="dark typography-h1 text-primary max-w-[37rem]">
                  About this connection
                </h1>
              </div>
            )}
          </>
        )}

        {isLoading ? (
          <Skeleton className="h-20 w-[80%] mt-6 rounded-md" />
        ) : (
          <>
            {reason && (
              <p className="typography-body text-primary mt-6 max-w-[37rem]">
                {reason}
              </p>
            )}
          </>
        )}

        <div className="mt-12">
          {/* Scores Section */}
          {isLoading ? (
            <Skeleton className="h-8 w-[70%] rounded-md mt-6" />
          ) : (
            <>
              {result && (
                <div className="flex flex-col gap-4">
                  <h1 className="dark typography-h1 text-primary max-w-[37rem] flex gap-1 items-baseline">
                    Your Result
                    <InfoSheet
                      title={INFO_SHEET_CONTENT.yourResult.title}
                      description= {INFO_SHEET_CONTENT.yourResult.description}
                    />
                  </h1>
                </div>
              )}
            </>
          )}

          {isLoading ? (
            <Skeleton className="h-20 w-[80%] mt-6 rounded-md" />
          ) : (
            <>
              {result && (
                <>
                  <p className="typography-body text-primary mt-6 max-w-[37rem]">
                    {result}
                  </p>
                  <p className="typography-body text-primary my-6 max-w-[37rem]">
                    Learn more about this score and your recommendations to improve it.
                  </p>
                </>
              )}
            </>
          )}
          {isLoading ? (
            <ScoresSkeleton />
          ) : (
            <div className='max-w-[37rem]'>
              <ConditionScoreCard
                rank={rank}
                score={score}
                rankColor={rank_color}
                name={name}
                onClick={() => navigate(`/health/l4/${scoreId}`)}
              />
            </div>
          )}
          {/* Researches Section */}
          {isLoading ? (
            <ResearchSkeleton />
          ) : links?.length > 0 ? (
            <div className="mt-12">
              <p className="typography-title text-primary">
                Research to support this connection
              </p>
              <div className="px-5 pt-5 pb-0.5 rounded-sm bg-[#42434D4D] mt-6">
                {links?.map((research, index) => (
                  <>
                    {(research.length > 0) && (
                      <div key={`${research}-${index}`}>
                        <button
                          className="w-full items-center justify-center transition-all"
                          onClick={() => onClickData(research)}
                        >
                          <div className="flex justify-between gap-6 mb-4">
                            <p
                              className="typography-body text-secondary truncate whitespace-nowrap"
                              title={research}
                            >
                              {research}
                            </p>
                            <ChevronRightIcon
                              height={24}
                              width={24}
                              className="text-secondary"
                            />
                          </div>
                        </button>
                        {index < links.length - 1 && (
                          <Separator className="bg-stroke-primary-subtle mb-4" />
                        )}
                      </div>
                    )}
                  </>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-12">
              <p className="italic typography-body text-primary">
                Original research by Viome based on user analysis, 2025.
              </p>
            </div>
          )}
        </div>
      </StandardContainer>
    </div>
  );
};
